/**
 * WebViewModal
 *
 * Detecta si el cotizador está siendo abierto desde el navegador interno
 * de WhatsApp (WebView) y muestra un modal bloqueante para instruir al usuario
 * a continuar desde su navegador nativo.
 *
 * Flujo Android:
 *   1. Se detecta WebView de WhatsApp mediante "WhatsApp" o "wv" en el User Agent.
 *   2. Se muestra el modal con dos botones:
 *      - "Abrir en navegador": redirige automáticamente usando un Intent URI de Android.
 *      - "Copiar link": copia la URL actual al portapapeles para que el usuario
 *        la pegue manualmente en su navegador.
 *
 * Flujo iOS:
 *   1. Se detecta WebView de WhatsApp mediante "WAiOS" en el User Agent.
 *   2. Se muestra el modal con instrucciones manuales:
 *      - Tocar los tres puntos (⋮) en la esquina superior derecha.
 *      - Seleccionar "Abrir en navegador".
 *      - Alternativamente, copiar el link con el botón "Copiar link".
 *   NOTA: iOS no permite abrir Safari programáticamente desde un WebView,
 *   por eso la redirección automática no es posible en este sistema operativo.
 *
 * Identificadores de WhatsApp en User Agent:
 *   - Android WebView genérico : "wv"
 *   - Android WhatsApp         : "WhatsApp"
 *   - iOS WhatsApp             : "WAiOS"
 */

import React, { useEffect, useState } from "react";
import { Modal, Button, message } from "antd";
import { Colors, Fonts } from "../../utils/constants";
import { nexBttn, backBttn } from "../../styles/StepsStyles";
import Logo from "../../assets/whiteLogoSvg.svg";

const COTIZADOR_URL = window.location.href;

const detectEnvironment = () => {
  const ua = navigator.userAgent;
  const android = ua.includes("Android");
  const isWhatsApp =
    ua.includes("WAiOS") ||  // WhatsApp iOS
    ua.includes("WA4A") ||   // WhatsApp Android
    ua.includes("WhatsApp") ||
    (android && ua.includes("wv"));

  return { isAndroid: android, isWhatsApp };
};

const WebViewModal: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);

  useEffect(() => {
    const { isAndroid: android, isWhatsApp } = detectEnvironment();
    if (isWhatsApp) {
      setIsAndroid(android);
      setVisible(true);
    }
  }, []);

  const openInBrowser = () => {
    const host = COTIZADOR_URL.replace("https://", "");
    window.location.href = `intent://${host}#Intent;scheme=https;action=android.intent.action.VIEW;end`;
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(COTIZADOR_URL);
      message.success("Link copiado al portapapeles");
    } catch {
      message.error("No se pudo copiar el link");
    }
  };

  return (
    <>
      <style>{`
        .webview-modal .ant-modal-content {
          background-color: ${Colors.blue};
          border-radius: 12px;
          padding: 0;
          box-shadow: 0 8px 32px rgba(0,0,0,0.5);
        }
        .webview-modal .ant-modal-body {
          padding: 0;
        }
      `}</style>

      <Modal
        open={visible}
        footer={null}
        closable={false}
        centered
        maskClosable={false}
        wrapClassName="webview-modal"
        width="90vw"
        style={{ maxWidth: 480 }}
        maskStyle={{ backgroundColor: "#5a6472", opacity: 1 }}
      >
        <div
          style={{
            backgroundColor: Colors.blue,
            borderRadius: 12,
            padding: "clamp(36px, 8vh, 96px) 28px clamp(32px, 8vh, 92px)",
            textAlign: "center",
          }}
        >
          <img
            src={Logo}
            alt="Logo"
            style={{ maxWidth: 180, marginBottom: 48, marginTop: -24 }}
          />

          <p
            style={{
              fontFamily: Fonts.sspBold,
              fontSize: 22,
              color: "white",
              marginBottom: 10,
              lineHeight: 1.3,
            }}
          >
            Para cotizar correctamente, abre este cotizador en tu navegador
          </p>

          {isAndroid ? (
            <>
              <Button
                shape="round"
                style={{ ...backBttn, marginTop: 32 }}
                onClick={openInBrowser}
              >
                Abrir en navegador
              </Button>
              <p
                style={{
                  fontFamily: Fonts.sspLight,
                  fontSize: 14,
                  color: "rgba(255,255,255,0.7)",
                  marginTop: 32,
                  marginBottom: 32,
                }}
              >
                O si lo prefieres, copia el link y pégalo en tu navegador:
              </p>
              <Button
                shape="round"
                style={{ ...nexBttn }}
                onClick={copyLink}
              >
                Copiar link
              </Button>
            </>
          ) : (
            <>
              <p
                style={{
                  fontFamily: Fonts.sspLight,
                  fontSize: 15,
                  color: "white",
                  marginBottom: 20,
                }}
              >
                Para abrirlo en tu navegador, sigue estos pasos:
              </p>
              <div
                style={{
                  backgroundColor: "rgba(255,255,255,0.1)",
                  borderRadius: 8,
                  padding: "14px 18px",
                  marginBottom: 28,
                  textAlign: "left",
                  border: "1px solid rgba(255,255,255,0.2)",
                }}
              >
                <p
                  style={{
                    fontFamily: Fonts.sspRegular,
                    fontSize: 14,
                    color: "white",
                    marginBottom: 10,
                    lineHeight: "1.8",
                  }}
                >
                  1. Toca el ícono{" "}
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ verticalAlign: "middle", display: "inline-block", position: "relative", top: -2 }}
                  >
                    <circle cx="12" cy="12" r="11" stroke="white" strokeWidth="1.5" />
                    <circle cx="7" cy="12" r="1.5" fill="white" />
                    <circle cx="12" cy="12" r="1.5" fill="white" />
                    <circle cx="17" cy="12" r="1.5" fill="white" />
                  </svg>
                  {" "}en la esquina superior derecha
                </p>
                <p
                  style={{
                    fontFamily: Fonts.sspRegular,
                    fontSize: 14,
                    color: "white",
                    marginBottom: 0,
                  }}
                >
                  2. Selecciona <strong>"Abrir en navegador"</strong>
                </p>
              </div>
              <p
                style={{
                  fontFamily: Fonts.sspLight,
                  fontSize: 14,
                  color: "white",
                  marginBottom: 20,
                }}
              >
                O si lo prefieres, copia el link y pégalo en tu navegador:
              </p>
              <Button shape="round" style={{ ...nexBttn }} onClick={copyLink}>
                Copiar link
              </Button>
            </>
          )}
        </div>
      </Modal>
    </>
  );
};

export default WebViewModal;
